import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

const ADMIN_USER_IDS = [
  'b362e9ab-12c7-4a78-af64-410288835c20', // BetaAdmin
];

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const AdminLocationDisputes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminNotes, setAdminNotes] = useState({});
  const [resolutionNotes, setResolutionNotes] = useState({});

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) { navigate('/'); return; }

      const { data: profile } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (profile && ADMIN_USER_IDS.includes(profile.id)) {
        setIsAdmin(true);
      } else if (ADMIN_USER_IDS.length === 0) {
        setIsAdmin(true);
      } else {
        navigate('/');
      }
    };
    checkAdmin();
  }, [user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchDisputes = async () => {
      setLoading(true);

      let query = supabase
        .from('location_disputes')
        .select(`
          *,
          location:locations(id, store_name, address, city, state, zip_code, owner_id),
          disputant:users!location_disputes_disputant_id_fkey(id, username, email),
          current_owner:users!location_disputes_current_owner_id_fkey(id, username, email)
        `)
        .order('submitted_at', { ascending: false });

      if (filter !== 'all') {
        if (filter === 'pending') {
          query = query.in('status', ['pending', 'reviewing']);
        } else if (filter === 'resolved') {
          query = query.in('status', ['resolved_transfer', 'resolved_denied']);
        }
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error fetching disputes:', error);
      } else {
        setDisputes(data || []);
        const notes = {};
        const resNotes = {};
        (data || []).forEach(d => {
          if (d.admin_notes) notes[d.id] = d.admin_notes;
          if (d.resolution_notes) resNotes[d.id] = d.resolution_notes;
        });
        setAdminNotes(notes);
        setResolutionNotes(resNotes);
      }

      setLoading(false);
    };

    fetchDisputes();
  }, [isAdmin, filter]);

  const getAdminProfileId = async () => {
    const { data } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    return data?.id;
  };

  const handleTransferOwnership = async (dispute) => {
    const resolution = resolutionNotes[dispute.id]?.trim();
    if (!resolution) {
      alert('Please provide resolution notes before transferring');
      return;
    }

    setProcessing(dispute.id);

    try {
      const adminId = await getAdminProfileId();
      const now = new Date().toISOString();

      // Get disputant's auth_id for setting owner_id
      const { data: disputantUser } = await supabase
        .from('users')
        .select('auth_id')
        .eq('id', dispute.disputant_id)
        .single();

      if (!disputantUser?.auth_id) throw new Error('Could not find disputant auth ID');

      // Remove current owner's staff record
      if (dispute.current_owner_id) {
        await supabase
          .from('location_staff')
          .update({ status: 'removed' })
          .eq('location_id', dispute.location_id)
          .eq('user_id', dispute.current_owner_id)
          .eq('role', 'owner');
      }

      // Transfer location ownership (triggers auto-create staff record)
      await supabase
        .from('locations')
        .update({
          owner_id: disputantUser.auth_id,
          reviewed_at: now,
          reviewed_by: adminId,
        })
        .eq('id', dispute.location_id);

      // Update dispute status
      await supabase
        .from('location_disputes')
        .update({
          status: 'resolved_transfer',
          resolution_notes: resolution,
          admin_notes: adminNotes[dispute.id]?.trim() || null,
          reviewed_at: now,
          reviewed_by: adminId,
        })
        .eq('id', dispute.id);

      // Deny any pending claims from the old owner for this location
      await supabase
        .from('location_claims')
        .update({ status: 'denied', denial_reason: 'Ownership transferred via dispute resolution' })
        .eq('location_id', dispute.location_id)
        .eq('status', 'pending');

      setDisputes(prev => prev.map(d =>
        d.id === dispute.id ? { ...d, status: 'resolved_transfer', reviewed_at: now } : d
      ));
    } catch (err) {
      console.error('Error transferring ownership:', err);
      alert('Failed to transfer ownership');
    }

    setProcessing(null);
  };

  const handleDenyDispute = async (dispute) => {
    const resolution = resolutionNotes[dispute.id]?.trim();
    if (!resolution) {
      alert('Please provide resolution notes before denying');
      return;
    }

    setProcessing(dispute.id);

    try {
      const adminId = await getAdminProfileId();
      const now = new Date().toISOString();

      await supabase
        .from('location_disputes')
        .update({
          status: 'resolved_denied',
          resolution_notes: resolution,
          admin_notes: adminNotes[dispute.id]?.trim() || null,
          reviewed_at: now,
          reviewed_by: adminId,
        })
        .eq('id', dispute.id);

      setDisputes(prev => prev.map(d =>
        d.id === dispute.id ? { ...d, status: 'resolved_denied', reviewed_at: now } : d
      ));
    } catch (err) {
      console.error('Error denying dispute:', err);
      alert('Failed to deny dispute');
    }

    setProcessing(null);
  };

  const saveAdminNotes = async (disputeId) => {
    await supabase
      .from('location_disputes')
      .update({ admin_notes: adminNotes[disputeId]?.trim() || null })
      .eq('id', disputeId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      reviewing: 'Under Review',
      resolved_transfer: 'Transferred',
      resolved_denied: 'Denied',
    };
    return labels[status] || status;
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      backgroundColor: theme.bgCard, padding: '12px 16px', display: 'flex',
      alignItems: 'center', gap: 12, borderBottom: `1px solid ${theme.border}`,
      position: 'sticky', top: 0, zIndex: 10,
    },
    backButton: {
      background: 'none', border: 'none', padding: 8, cursor: 'pointer',
      color: theme.accent, display: 'flex',
    },
    headerTitle: { fontSize: 18, fontWeight: 600, color: theme.text, margin: 0 },
    filterRow: {
      display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto',
      borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.bgCard,
    },
    filterBtn: {
      padding: '8px 16px', borderRadius: 20, border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard, color: theme.textSecondary, fontSize: 13,
      fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    filterBtnActive: { backgroundColor: theme.accent, borderColor: theme.accent, color: '#fff' },
    content: { padding: 16 },
    emptyState: { textAlign: 'center', padding: 40, color: theme.textMuted },
    card: {
      backgroundColor: theme.bgCard, borderRadius: 12, border: `1px solid ${theme.border}`,
      marginBottom: 12, overflow: 'hidden',
    },
    cardHeader: {
      padding: 12, borderBottom: `1px solid ${theme.border}`, display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
    },
    storeName: { fontSize: 16, fontWeight: 600, color: theme.text },
    date: { fontSize: 12, color: theme.textMuted },
    cardBody: { padding: 12 },
    infoRow: { marginBottom: 10 },
    infoLabel: { fontSize: 11, color: theme.textMuted, textTransform: 'uppercase' },
    infoValue: { fontSize: 13, color: theme.text },
    textArea: {
      width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput, color: theme.text, fontSize: 13,
      boxSizing: 'border-box', outline: 'none', minHeight: 60, resize: 'vertical',
      fontFamily: 'inherit', marginTop: 4,
    },
    saveBtn: {
      fontSize: 12, color: theme.accent, background: 'none', border: 'none',
      cursor: 'pointer', padding: '4px 0', marginTop: 4,
    },
    docLink: {
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13,
      color: theme.accent, textDecoration: 'none', padding: '4px 0',
    },
    actions: {
      padding: 12, borderTop: `1px solid ${theme.border}`, display: 'flex', gap: 8,
    },
    transferBtn: {
      flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
      backgroundColor: '#f59e0b', color: '#fff', fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
    denyBtn: {
      flex: 1, padding: '10px 16px', borderRadius: 8, border: `1px solid ${theme.error}`,
      backgroundColor: 'transparent', color: theme.error, fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
    statusBadge: { padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
    statusTransfer: { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
    statusDenied: { backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
    statusPending: { backgroundColor: 'rgba(250, 204, 21, 0.15)', color: '#eab308' },
    loading: { textAlign: 'center', padding: 40, color: theme.textMuted },
  };

  if (!isAdmin) {
    return <div style={styles.container}><div style={styles.loading}>Checking permissions...</div></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}><BackIcon /></button>
        <h1 style={styles.headerTitle}>Location Disputes</h1>
      </div>

      <div style={styles.filterRow}>
        {['pending', 'resolved', 'all'].map((f) => (
          <button
            key={f}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading disputes...</div>
        ) : disputes.length === 0 ? (
          <div style={styles.emptyState}>No {filter === 'all' ? '' : filter} disputes found</div>
        ) : (
          disputes.map((dispute) => {
            const isResolved = dispute.status.startsWith('resolved');

            return (
              <div key={dispute.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.storeName}>{dispute.location?.store_name || 'Unknown'}</div>
                    <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
                      {dispute.location?.address}, {dispute.location?.city}, {dispute.location?.state}
                    </div>
                  </div>
                  <span style={styles.date}>{formatDate(dispute.submitted_at)}</span>
                </div>

                <div style={styles.cardBody}>
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Disputant</div>
                    <div style={styles.infoValue}>
                      {dispute.full_name} (@{dispute.disputant?.username || 'unknown'})
                    </div>
                  </div>
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Current Owner</div>
                    <div style={styles.infoValue}>
                      @{dispute.current_owner?.username || 'unknown'} ({dispute.current_owner?.email})
                    </div>
                  </div>
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Role Claimed</div>
                    <div style={styles.infoValue}>{dispute.role_at_business}</div>
                  </div>
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Contact</div>
                    <div style={styles.infoValue}>
                      {dispute.contact_email}
                      {dispute.contact_phone && ` | ${dispute.contact_phone}`}
                    </div>
                  </div>
                  <div style={styles.infoRow}>
                    <div style={styles.infoLabel}>Evidence Description</div>
                    <div style={{ ...styles.infoValue, fontStyle: 'italic', color: theme.textSecondary }}>
                      "{dispute.evidence_description}"
                    </div>
                  </div>

                  {/* Documents */}
                  {dispute.document_urls?.length > 0 && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Documents ({dispute.document_urls.length})</div>
                      {dispute.document_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={styles.docLink}>
                          <FileIcon /> Document {i + 1}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Status */}
                  {isResolved && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Resolution</div>
                      <span style={{
                        ...styles.statusBadge,
                        ...(dispute.status === 'resolved_transfer' ? styles.statusTransfer : styles.statusDenied),
                      }}>
                        {getStatusLabel(dispute.status)}
                      </span>
                    </div>
                  )}

                  {dispute.resolution_notes && isResolved && (
                    <div style={styles.infoRow}>
                      <div style={styles.infoLabel}>Resolution Notes</div>
                      <div style={styles.infoValue}>{dispute.resolution_notes}</div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div style={{ marginTop: 12 }}>
                    <div style={styles.infoLabel}>Admin Notes</div>
                    <textarea
                      placeholder="Internal notes..."
                      value={adminNotes[dispute.id] || ''}
                      onChange={(e) => setAdminNotes(prev => ({ ...prev, [dispute.id]: e.target.value }))}
                      style={styles.textArea}
                    />
                    <button style={styles.saveBtn} onClick={() => saveAdminNotes(dispute.id)}>
                      Save Notes
                    </button>
                  </div>

                  {/* Resolution Notes (for pending) */}
                  {!isResolved && (
                    <div style={{ marginTop: 12 }}>
                      <div style={styles.infoLabel}>Resolution Notes (required for action)</div>
                      <textarea
                        placeholder="Explain the resolution decision..."
                        value={resolutionNotes[dispute.id] || ''}
                        onChange={(e) => setResolutionNotes(prev => ({ ...prev, [dispute.id]: e.target.value }))}
                        style={styles.textArea}
                      />
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!isResolved && (
                  <div style={styles.actions}>
                    <button
                      style={styles.transferBtn}
                      onClick={() => handleTransferOwnership(dispute)}
                      disabled={processing === dispute.id}
                    >
                      <CheckIcon />
                      {processing === dispute.id ? 'Processing...' : 'Transfer Ownership'}
                    </button>
                    <button
                      style={styles.denyBtn}
                      onClick={() => handleDenyDispute(dispute)}
                      disabled={processing === dispute.id}
                    >
                      <XIcon />
                      Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminLocationDisputes;
